<template>
    <Dialog>
        <DialogTrigger>
            <slot />
        </DialogTrigger>
        <DialogContent class="sm:max-w-[680px]">
            <DialogHeader>
                <DialogTitle class="gap-2 flex items-center">
                    {{ $t("workflows.assign_event_types") }}
                </DialogTitle>
                <p class="text-sm text-iron-600">
                    {{ $t("workflows.assign_event_types_description") }}
                </p>
            </DialogHeader>

            <div class="mt-4 space-y-3 px-4">
                <div class="relative">
                    <Input
                        v-model="search"
                        :placeholder="$t('workflows.search_event_types')"
                        class="rounded-md border-iron-300 bg-white px-3 py-2 text-sm shadow-sm w-full border focus:outline-none"
                    />
                </div>

                <div class="rounded-md border-iron-200 border">
                    <div
                        class="border-iron-200 px-3 py-2 text-sm text-iron-600 flex items-center justify-between border-b"
                    >
                        <span>{{
                            $t("workflows.event_types_selected", {
                                selected: selectedCount,
                                total: totalEventTypesCount,
                            })
                        }}</span>
                    </div>
                    <div class="p-2 max-h-[450px] overflow-auto">
                        <!-- Personal Event Types Section -->
                        <div
                            v-if="filteredPersonalEventTypes.length > 0"
                            class="mb-4"
                        >
                            <div class="mb-2 gap-2 flex items-center">
                                <h4 class="text-sm font-medium text-iron-900">
                                    {{ $t("workflows.personal_event_types") }}
                                </h4>
                                <span
                                    class="rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700"
                                >
                                    {{ $t("workflows.personal") }}
                                </span>
                            </div>
                            <div
                                v-for="et in filteredPersonalEventTypes"
                                :key="`personal-${et.id}`"
                                class="mb-2 gap-3 rounded-lg border-iron-200 p-3 hover:bg-iron-50 flex items-start border"
                                @click="toggle(et)"
                            >
                                <Checkbox :checked="isAssigned(et)" />
                                <div class="flex-1">
                                    <div class="gap-2 flex items-center">
                                        <div
                                            class="text-sm font-semibold text-iron-900"
                                        >
                                            {{ et.name }}
                                        </div>
                                        <span
                                            v-if="et.appearance?.theme"
                                            class="rounded-md bg-iron-100 px-2 py-0.5 font-medium text-iron-700 text-[10px]"
                                            >{{
                                                et.appearance.theme.toLowerCase()
                                            }}</span
                                        >
                                        <span
                                            v-if="!et.enabled_at"
                                            class="rounded-md bg-iron-100 px-2 py-0.5 font-medium text-iron-700 text-[10px]"
                                            >Inactive</span
                                        >
                                    </div>
                                    <div
                                        class="mt-1 gap-1 text-xs text-iron-600 flex items-center"
                                    >
                                        <Clock :size="12" />
                                        <span v-if="et.duration">
                                            {{ et.duration }} min</span
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Team Event Types Section -->
                        <div v-if="filteredTeamEventTypes.length" class="mb-4">
                            <div class="mb-2 gap-2 flex items-center">
                                <h4 class="text-sm font-medium text-iron-900">
                                    {{ $t("workflows.team_event_types") }}
                                </h4>
                                <span
                                    class="rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700"
                                >
                                    {{ $t("workflows.team") }}
                                </span>
                            </div>
                            <div
                                v-for="et in filteredTeamEventTypes"
                                :key="`team-${et.id}`"
                                class="mb-2 rounded-lg border-iron-200 hover:bg-iron-50 border"
                            >
                                <div
                                    class="gap-3 p-3 flex items-start"
                                    @click="toggle(et)"
                                >
                                    <Checkbox :checked="isAssigned(et)" />
                                    <div class="flex-1">
                                        <div class="gap-2 flex items-center">
                                            <div
                                                class="text-sm font-semibold text-iron-900"
                                            >
                                                {{ et.name }}
                                            </div>
                                            <span
                                                v-if="et.appearance?.theme"
                                                class="rounded-md bg-iron-100 px-2 py-0.5 font-medium text-iron-700 text-[10px]"
                                                >{{
                                                    et.appearance.theme.toLowerCase()
                                                }}</span
                                            >
                                            <span
                                                v-if="!et.enabled_at"
                                                class="rounded-md bg-iron-100 px-2 py-0.5 font-medium text-iron-700 text-[10px]"
                                                >Inactive</span
                                            >
                                        </div>
                                        <div
                                            class="mt-1 gap-1 text-xs text-iron-600 flex items-center"
                                        >
                                            <Clock :size="12" />
                                            <span v-if="et.duration">
                                                {{ et.duration }} min</span
                                            >
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="
                                        canAssignTeamWorkflows && isAssigned(et)
                                    "
                                    class="border-iron-100 px-3 pb-3 pl-11 pt-2 border-t"
                                >
                                    <div
                                        class="gap-2 flex items-center"
                                        @click.stop="toggleShared(et)"
                                    >
                                        <Checkbox
                                            :checked="
                                                isWorkflowSharedForEventType(et)
                                            "
                                        />
                                        <label
                                            class="text-xs text-iron-500 cursor-pointer"
                                        >
                                            {{
                                                $t("workflows.shared_with_team")
                                            }}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Legacy Event Types (for backward compatibility) -->
                        <div
                            v-if="filteredLegacyEventTypes.length"
                            class="mb-4"
                        >
                            <div
                                v-for="et in filteredLegacyEventTypes"
                                :key="`legacy-${et.id}`"
                                class="mb-2 gap-3 rounded-lg border-iron-200 p-3 hover:bg-iron-50 flex items-start border"
                                @click="toggle(et)"
                            >
                                <Checkbox :checked="isAssigned(et)" />
                                <div class="flex-1">
                                    <div class="gap-2 flex items-center">
                                        <div
                                            class="text-sm font-semibold text-iron-900"
                                        >
                                            {{ et.name }}
                                        </div>
                                        <span
                                            v-if="et.appearance?.theme"
                                            class="rounded-md bg-iron-100 px-2 py-0.5 font-medium text-iron-700 text-[10px]"
                                            >{{
                                                et.appearance.theme.toLowerCase()
                                            }}</span
                                        >
                                        <span
                                            v-if="!et.enabled_at"
                                            class="rounded-md bg-iron-100 px-2 py-0.5 font-medium text-iron-700 text-[10px]"
                                            >Inactive</span
                                        >
                                    </div>
                                    <div
                                        class="mt-1 gap-1 text-xs text-iron-600 flex items-center"
                                    >
                                        <Clock :size="12" />
                                        <span v-if="et.duration">
                                            {{ et.duration }} min</span
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <DialogFooter>
                <DialogClose>
                    <button
                        type="button"
                        class="rounded-md border-iron-300 bg-white px-4 py-2 text-sm font-medium text-iron-700 hover:bg-iron-50 border"
                    >
                        {{ $t("close") }}
                    </button>
                </DialogClose>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { ref, computed } from "vue";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "@/components/ui/dialog-v2";
import { Clock } from "lucide-vue-next";
import { useToast } from "@/components/ui/toast/use-toast";

const { toast } = useToast();

const props = defineProps<{
    workflowId: number;
    eventTypes: Array<any>;
    personalEventTypes?: Array<any>;
    teamEventTypes?: Array<any>;
    canAssignTeamWorkflows?: boolean;
}>();

const search = ref("");

// Computed properties for filtered event types by category
const filteredPersonalEventTypes = computed(() => {
    const eventTypes = props.personalEventTypes || [];
    if (!search.value) return eventTypes;
    const q = search.value.toLowerCase();
    return eventTypes.filter((et: any) =>
        String(et.name || "")
            .toLowerCase()
            .includes(q),
    );
});

const filteredTeamEventTypes = computed(() => {
    const eventTypes = props.teamEventTypes || [];
    if (!search.value) return eventTypes;
    const q = search.value.toLowerCase();
    return eventTypes.filter((et: any) =>
        String(et.name || "")
            .toLowerCase()
            .includes(q),
    );
});

// Legacy event types (for backward compatibility when personal/team separation not available)
const filteredLegacyEventTypes = computed(() => {
    // Only show legacy if neither personal nor team event types are provided
    if (props.personalEventTypes || props.teamEventTypes) return [];

    const eventTypes = props.eventTypes || [];
    if (!search.value) return eventTypes;
    const q = search.value.toLowerCase();
    return eventTypes.filter((et: any) =>
        String(et.name || "")
            .toLowerCase()
            .includes(q),
    );
});

const filteredEventTypes = computed(() => {
    // Maintain backward compatibility
    return [
        ...filteredPersonalEventTypes.value,
        ...filteredTeamEventTypes.value,
        ...filteredLegacyEventTypes.value,
    ];
});

const detachEventType = function (eventTypeId: number) {
    router.post(
        "/workflows/" + props.workflowId + "/event-types/detach",
        {
            eventTypeId: eventTypeId,
        },
        {
            onFinish: () => {},
        },
    );
};

const assignEventType = function (eventTypeId: number) {
    router.post(
        "/workflows/" + props.workflowId + "/event-types/assign",
        {
            eventTypeId: eventTypeId,
        },
        {
            onFinish: () => {},
        },
    );
};

const toggleShared = function (et: any) {
    router.post(
        "/workflows/" + props.workflowId + "/event-types/toggle-shared",
        {
            eventTypeId: et.id,
        },
        {
            onFinish: () => {},
        },
    );
};

const isWorkflowAlreadyAssigned = (id: number, assignedWorkflows: any) => {
    for (let i = 0; i < assignedWorkflows.length; i++) {
        if (assignedWorkflows[i].id === id) {
            return true;
        }
    }
    return false;
};

const isAssigned = (et: any) =>
    isWorkflowAlreadyAssigned(props.workflowId, et.workflows);

const isWorkflowSharedForEventType = (et: any) => {
    const workflow = et.workflows?.find((w: any) => w.id === props.workflowId);
    return workflow?.pivot?.user_id === null;
};

const totalEventTypesCount = computed(() => {
    if (props.personalEventTypes || props.teamEventTypes) {
        return (
            (props.personalEventTypes || []).length +
            (props.teamEventTypes || []).length
        );
    }
    return (props.eventTypes || []).length;
});

const selectedCount = computed(() => {
    return filteredEventTypes.value.reduce(
        (acc: number, et: any) => acc + (isAssigned(et) ? 1 : 0),
        0,
    );
});

const toggle = (et: any) => {
    if (isAssigned(et)) detachEventType(et.id);
    else assignEventType(et.id);
};
</script>

<style></style>
